import { SidebarProvider } from "@quake/shared-ui/components/ui/sidebar";
import { queryClient, trpc } from "~/utils/trpc";
import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { PinnedCandidateDocumentsProvider } from "~/components/candidates/pinned-candidate-documents";
import { DashboardTaskbar } from "~/components/dashboard-taskbar";
import { AppSidebar } from "~/components/app-sidebar";
import { Fragment, useEffect, type CSSProperties } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@quake/shared-ui/components/ui/breadcrumb";
import { useBreadcrumbsContext } from "~/contexts/BreadcrumbsContext";
import { AutomationTrackerProvider } from "~/contexts/AutomationTrackerContext";
import { FullScreenDialog } from "~/components/FullScreenDialog";
import { useLocation } from "@tanstack/react-router";
import { AgentPanelProvider, useAgentPanel } from "~/contexts/AgentPanelContext";
import { QuakeUiSurfacesProvider } from "~/components/quake-ui-surfaces";
import { getDashboardRouteTitle, useDocumentTitle } from "~/utils/document-title";
import { useQuery } from "@tanstack/react-query";
import { usePermissionStore } from "~/store/usePermissionStore";
import { DesktopNotifications } from "~/components/desktop-notifications/desktop-notifications";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import { useLocalStorage } from "usehooks-ts";

const MAIN_SIDEBAR_OPEN_STORAGE_KEY = "quake:main-sidebar-open";

export const Route = createFileRoute("/_dashboard")({
  component: RouteComponent,
  beforeLoad: async ({ location }) => {
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions()).catch(() => null);
    if (!session?.isLoggedIn) {
      throw redirect({ to: "/login", search: { redirectTo: location.href } });
    }
    if (session.authStage === "first_factor") {
      throw redirect({ to: "/select-dataset", search: { redirectTo: location.href } });
    }
    if (session.authStage === "mfa_pending") {
      throw redirect({ to: "/mfa", search: { redirectTo: location.href } });
    }
  },
});

function RouteComponent() {
  const { data: session } = useQuery(trpc.auth.getSession.queryOptions());
  const scopeKey = `${session?.user?.id ?? ""}:${session?.dataset?.id ?? ""}`;
  return (
    <PermissionsGate>
      <AgentPanelProvider scopeKey={scopeKey}>
        <PinnedCandidateDocumentsProvider scopeKey={scopeKey}>
          <QuakeUiSurfacesProvider>
            <DesktopNotifications>
              <DashboardLayout />
            </DesktopNotifications>
          </QuakeUiSurfacesProvider>
        </PinnedCandidateDocumentsProvider>
      </AgentPanelProvider>
    </PermissionsGate>
  );
}

function PermissionsGate({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useQuery(trpc.auth.getSession.queryOptions());
  const hydratePermissions = usePermissionStore((state) => state.hydratePermissions);
  const isPermissionsLoaded = usePermissionStore((state) => state.isPermissionsLoaded);
  const permissionsDatasetId = usePermissionStore((state) => state.permissionsDatasetId);
  const sessionDatasetId = session?.dataset?.id ?? null;

  useEffect(() => {
    if (isPending) return;

    hydratePermissions({
      permissions: session?.user?.permissions || [],
      role: session?.user?.role || "",
      email: session?.user?.email || "",
      name: session?.user?.name || "",
      datasetId: sessionDatasetId,
    });
  }, [hydratePermissions, isPending, session, sessionDatasetId]);

  if (isPending || !isPermissionsLoaded || permissionsDatasetId !== sessionDatasetId) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading permissions">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  return children;
}

function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAgentPanelOpen } = useAgentPanel();
  // Remember the user's last collapse choice in this browser across reloads and pages.
  const [isMainSidebarOpen, setIsMainSidebarOpen] = useLocalStorage(MAIN_SIDEBAR_OPEN_STORAGE_KEY, true);
  useDocumentTitle(getDashboardRouteTitle(location.pathname), undefined, 0);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);

      if (elementAtPoint?.closest(".ignore-global-drag")) {
        return;
      }

      const chatDropZone = elementAtPoint?.closest('[data-chat-drop-zone="true"]');

      if (chatDropZone) {
        window.dispatchEvent(new CustomEvent("quake:chat-file-drop", { detail: { file, dropZone: chatDropZone } }));
        return;
      }

      window.dispatchEvent(new CustomEvent("quake:open-file-parser", { detail: { file } }));
    }
  };
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter((segment) => segment !== "");
    if (pathSegments.length === 1) {
      navigate({ to: `/${location.pathname.split("/")[1]}/chats` });
    }
  }, []);
  const { breadCrumbs } = useBreadcrumbsContext();
  return (
    <>
      <AutomationTrackerProvider>
        <FullScreenDialog isAgentPanelOpen={isAgentPanelOpen} />
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="flex-col sidebar-provider min-h-screen relative"
        >
          <SidebarProvider
            open={isMainSidebarOpen}
            onOpenChange={setIsMainSidebarOpen}
            className="h-dvh min-h-0 overflow-hidden"
            style={
              {
                "--sidebar-width": "17.5rem",
                "--dashboard-taskbar-height": "calc(2.5rem + env(safe-area-inset-bottom))",
                "--dashboard-content-height": "calc(100dvh - var(--dashboard-taskbar-height))",
              } as CSSProperties
            }
          >
            <AppSidebar />
            <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col contain-size">
              <main data-dashboard-background className="flex min-h-0 flex-1 flex-col overflow-auto overscroll-y-none">
                {(breadCrumbs?.length ?? 0) < 2 ? null : (
                  <div className="flex shrink-0 items-center px-4 py-2">
                    <Breadcrumb>
                      <BreadcrumbList>
                        {breadCrumbs.map((item, index) => (
                          <Fragment key={item.link + item.title + index}>
                            <BreadcrumbItem>
                              <BreadcrumbLink asChild>
                                <Link to={item.link}>{item.title}</Link>
                              </BreadcrumbLink>
                            </BreadcrumbItem>
                            {index + 1 !== breadCrumbs.length && <BreadcrumbSeparator />}
                          </Fragment>
                        ))}
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>
                )}
                <Outlet />
              </main>
              <DashboardTaskbar />
            </div>
          </SidebarProvider>
        </div>
      </AutomationTrackerProvider>
    </>
  );
}
